import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import Seo from "../components/Seo";
import {
  Phone,
  MessageCircle,
  ShieldCheck,
  Award,
  Users,
  HardHat,
  Star,
  ArrowRight,
  Camera,
  Network,
  Eye,
  Wrench,
  MapPin,
  CheckCircle2,
  Zap,
  Clock,
} from "lucide-react";
import { BRAND, WHATSAPP_LINK, CALL_SALES, SERVICES, STATS } from "../lib/constants";
import { absoluteUrl } from "../lib/adminApi";
import QuoteDialog from "../components/QuoteDialog";
import HeroSlider from "../components/HeroSlider";
import RecentWork from "../components/RecentWork";
import Lightbox from "../components/Lightbox";
import FollowUs from "../components/FollowUs";
import ClientsMarquee from "../components/ClientsMarquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceIcons = {
  cctv: Camera,
  networking: Network,
  surveillance: Eye,
  amc: Wrench,
};

function Counter({ to, suffix = "" }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const duration = 1600;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return (
    <span>
      {val.toLocaleString()}
      {suffix}
    </span>
  );
}

export default function Home() {
  const [reviews, setReviews] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteService, setQuoteService] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    axios.get(`${API}/gallery`).then((r) => {
      const imgs = (r.data.images || []).map((img) => ({ ...img, url: absoluteUrl(img.url) }));
      setGallery(imgs);
    }).catch(() => {});
  }, []);

  const openQuote = (service) => {
    setQuoteService(service || null);
    setQuoteOpen(true);
  };

  return (
    <div data-testid="home-page">
      <Seo
        title="Best CCTV Installation in Nagpur"
        description="Hayat Security is Nagpur's most trusted CCTV installation, surveillance, networking & AMC company. 15+ years experience · 10,000+ clients across Sitabuldi, Dharampeth, Civil Lines & all of Nagpur. Call +91 96738 78652."
        canonicalPath="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 hero-grid-bg pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 md:pt-20 md:pb-28 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E8F4F8] text-[#1C6B8A] text-xs font-semibold mb-5" data-testid="hero-badge">
                <ShieldCheck className="w-3.5 h-3.5" />
                Trusted CCTV & Security Experts · Nagpur
              </div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
                Professional <span className="text-[#1C6B8A]">CCTV</span> & Security
                <br className="hidden sm:block" /> Installation Services in Nagpur
              </h1>
              <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
                CCTV · Networking · Surveillance · AMC — engineered, installed and maintained by a team with 15+ years of on-ground experience. Think Security, Think Hayat.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={() => openQuote()}
                  data-testid="hero-get-quote-btn"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full brand-btn text-sm font-semibold"
                >
                  Get Free Quote <ArrowRight className="w-4 h-4" />
                </button>
                <a
                  href={CALL_SALES}
                  data-testid="hero-call-btn"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full brand-outline-btn text-sm font-semibold"
                >
                  <Phone className="w-4 h-4" />
                  Call Sales
                </a>
                <a
                  href={WHATSAPP_LINK()}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-whatsapp-btn"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full border-1.5 border-emerald-500 text-emerald-600 hover:bg-emerald-50 text-sm font-semibold transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#1C6B8A]" />
                  Free site survey
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#1C6B8A]" />
                  In-house certified technicians
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#1C6B8A]" />
                  Transparent pricing
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="lg:col-span-5 relative"
            >
              <HeroSlider slides={gallery.slice(0, 5)} />

              {/* Floating rating card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="absolute -bottom-5 -left-5 md:-left-10 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 flex items-center gap-3 z-20"
                data-testid="hero-rating-card"
              >
                <div className="flex -space-x-2">
                  {["TS", "TJ", "SH"].map((i) => (
                    <div key={i} className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1C6B8A] to-[#155670] text-white text-[11px] font-semibold flex items-center justify-center ring-2 ring-white">
                      {i}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                    <span className="ml-1 text-xs font-semibold text-slate-900">4.9</span>
                  </div>
                  <div className="text-[11px] text-slate-500 font-medium">310+ Google reviews</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST STATS */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="trust-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Award, value: STATS[0].value, suffix: "+", label: "Years of Experience", sub: "Serving Nagpur since 2010" },
              { icon: Users, value: STATS[1].value, suffix: "+", label: "Happy Clients", sub: "Homes, offices & retail" },
              { icon: HardHat, value: STATS[2].value, suffix: "+", label: "Projects Completed", sub: "Across Central India" },
            ].map((s, idx) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  data-testid={`stat-${idx}`}
                  className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 flex items-start gap-5 card-soft"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#E8F4F8] text-[#1C6B8A] flex items-center justify-center shrink-0">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-display text-3xl md:text-4xl font-bold text-slate-900">
                      <Counter to={s.value} suffix={s.suffix} />
                    </div>
                    <div className="font-semibold text-slate-800 mt-0.5">{s.label}</div>
                    <div className="text-sm text-slate-500 mt-1">{s.sub}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-14">
            <div className="max-w-2xl">
              <div className="text-sm font-semibold text-[#1C6B8A] uppercase tracking-wider">What we do</div>
              <h2 className="font-display text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mt-2">
                Security solutions, end-to-end
              </h2>
              <p className="text-slate-600 mt-4 leading-relaxed">
                From the first site survey to the last cable label — our team handles every step in-house. No subcontractors, no surprises.
              </p>
            </div>
            <Link
              to="/services"
              data-testid="services-view-all"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1C6B8A] hover:text-[#155670]"
            >
              View all services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s, idx) => {
              const Icon = serviceIcons[s.id];
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  data-testid={`service-card-${s.id}`}
                  className="group bg-white border border-slate-200 rounded-2xl p-6 card-soft hover:-translate-y-1 transition-all duration-200"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#E8F4F8] text-[#1C6B8A] flex items-center justify-center mb-5 group-hover:bg-[#1C6B8A] group-hover:text-white transition-colors">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-slate-900">{s.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.short}</p>
                  <button
                    onClick={() => openQuote(s.title)}
                    data-testid={`service-quote-${s.id}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1C6B8A] hover:gap-2.5 transition-all"
                  >
                    Get Quote <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CLIENTS MARQUEE */}
      <ClientsMarquee />

      {/* GOOGLE RATING */}
      <section className="bg-slate-50" data-testid="google-reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-28">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18A10.97 10.97 0 0 0 1 12c0 1.78.43 3.47 1.18 4.93l3.66-2.83z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
                Verified Google Reviews
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900">
                What our clients in Nagpur say
              </h2>
              <div className="mt-6 flex items-end gap-4">
                <div className="font-display text-6xl font-bold text-slate-900">
                  {reviews?.rating || 4.9}
                </div>
                <div className="pb-2">
                  <div className="flex items-center gap-0.5 mb-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <div className="text-sm text-slate-600 font-medium">
                    Based on {reviews?.total || 310}+ reviews
                  </div>
                </div>
              </div>
              <a
                href={BRAND.googleBusiness}
                target="_blank"
                rel="noreferrer"
                data-testid="view-on-google-btn"
                className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full brand-outline-btn text-sm font-semibold"
              >
                View on Google <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              {(reviews?.reviews || []).slice(0, 4).map((r, idx) => (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                  data-testid={`review-card-${r.id}`}
                  className="bg-white border border-slate-100 rounded-2xl p-6 card-soft"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1C6B8A] to-[#155670] text-white text-sm font-semibold flex items-center justify-center">
                      {r.initial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 text-sm truncate">{r.author}</div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <div className="flex">
                          {[...Array(r.rating)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                          ))}
                        </div>
                        <span>·</span>
                        <span>{r.time}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{r.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/35290669/pexels-photo-35290669.jpeg"
                alt="Technician working"
                className="w-full h-[420px] object-cover rounded-3xl border border-slate-200"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#1C6B8A] text-white rounded-2xl p-5 shadow-xl hidden sm:block">
                <div className="font-display text-3xl font-bold">15+</div>
                <div className="text-xs opacity-90">Years on the field</div>
              </div>
            </div>
            <div>
              <div className="text-sm font-semibold text-[#1C6B8A] uppercase tracking-wider">Why Hayat</div>
              <h2 className="font-display text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mt-2">
                Trusted security partner for Nagpur
              </h2>
              <p className="text-slate-600 mt-4 leading-relaxed">
                Founded and led by Murtaza Hasan, Hayat has grown from a small neighborhood service into one of Nagpur's most trusted names in CCTV and networking — one installation at a time.
              </p>

              <div className="mt-8 space-y-5">
                {[
                  { icon: Zap, title: "Fast on-site response", desc: "Quick surveys and installations — most homes done in a day." },
                  { icon: ShieldCheck, title: "Quality hardware only", desc: "We recommend brands we service — no shortcuts on critical components." },
                  { icon: Clock, title: "Strong after-service", desc: "Reliable AMC and repairs so your system keeps running year-round." },
                ].map((f) => {
                  const Icon = f.icon;
                  return (
                    <div key={f.title} className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#E8F4F8] text-[#1C6B8A] flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">{f.title}</div>
                        <div className="text-sm text-slate-600 mt-0.5">{f.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => openQuote()}
                data-testid="why-us-cta"
                className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-full brand-btn text-sm font-semibold"
              >
                Request Site Visit <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* RECENT INSTALLATIONS SLIDER */}
      <RecentWork images={gallery} onOpenImage={(i) => setLightboxIdx(i)} />

      {/* FOLLOW US */}
      <FollowUs />

      {/* GALLERY PREVIEW */}
      <section className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="text-sm font-semibold text-[#1C6B8A] uppercase tracking-wider">Our work</div>
              <h2 className="font-display text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mt-2">
                Recent installations across Nagpur
              </h2>
            </div>
            <Link
              to="/projects"
              data-testid="gallery-view-all"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1C6B8A]"
            >
              View full gallery <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {gallery.slice(0, 8).map((img, idx) => (
              <motion.div
                key={img.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                className={`relative overflow-hidden rounded-xl border border-slate-200 bg-slate-900 ${idx === 0 || idx === 5 ? "row-span-2 col-span-2" : ""}`}
                data-testid={`gallery-preview-${img.id}`}
              >
                <img
                  src={img.url}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60 aspect-square"
                />
                <img
                  src={img.url}
                  alt={img.title}
                  className="relative w-full h-full object-contain aspect-square hover:scale-105 transition-transform duration-500"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCAL AREA */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="rounded-3xl overflow-hidden border border-slate-200 grid lg:grid-cols-2">
            <div className="p-8 md:p-12 bg-gradient-to-br from-[#1C6B8A] to-[#0f4a63] text-white">
              <MapPin className="w-7 h-7 mb-4" />
              <h3 className="font-display text-2xl md:text-3xl font-bold leading-tight">
                Serving Nagpur & nearby areas
              </h3>
              <p className="mt-3 text-white/80 leading-relaxed">
                Sitabuldi · Dharampeth · Itwari · Sadar · Civil Lines · Ramdaspeth · Wardhaman Nagar · Hingna · Manish Nagar · Koradi · and more.
              </p>
              <a
                href={CALL_SALES}
                data-testid="local-call-btn"
                className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-[#1C6B8A] font-semibold hover:bg-slate-100"
              >
                <Phone className="w-4 h-4" /> Call {BRAND.salesDisplay}
              </a>
            </div>
            <div className="p-8 md:p-12 bg-white flex flex-col justify-center">
              <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight">
                Need a site visit before deciding?
              </h3>
              <p className="mt-3 text-slate-600">
                Our engineers will visit your premises, assess camera placements, network points and give you a transparent, written quote — no obligation.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => openQuote()}
                  data-testid="local-site-visit-btn"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full brand-btn font-semibold"
                >
                  Request Site Visit
                </button>
                <a
                  href={WHATSAPP_LINK("Hi, I'd like to book a site visit.")}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="local-whatsapp-btn"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full border-1.5 border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} defaultService={quoteService} />
      <Lightbox
        images={gallery}
        index={lightboxIdx}
        onClose={() => setLightboxIdx(null)}
        onPrev={() => setLightboxIdx((i) => (i - 1 + gallery.length) % gallery.length)}
        onNext={() => setLightboxIdx((i) => (i + 1) % gallery.length)}
      />
    </div>
  );
}
